/**
 * The values of a set of mirrored rows, in one query.
 *
 * `evaluateFormula` is shared with the server and stays synchronous, so it
 * cannot go and fetch the row a reference points at: both sides load what they
 * need first and hand it a reader. This is the device's load for a whole set of
 * ids at once — the pairs a recipe's hops name — and it is one statement
 * whatever the number of hops.
 *
 * IT IS ITS OWN FILE FOR A REASON THAT IS NOT TIDINESS. `@/lib/db` opens
 * `expo-sqlite`, which cannot be imported under bun at all, so any pure rule
 * that reaches the mirror can only be tested by replacing a module. Replacing
 * `@/lib/db` replaces it for every test in the process, including the one suite
 * that runs the real migrations against a real database — a whole file's worth
 * of proof lost to one unrelated test's stub. Replacing THIS module cannot harm
 * anything, because this is all it does and nothing else needs it stubbed.
 *
 * `formula-fill.ts` keeps its own copy of the same read. That one fills a row
 * being saved and predates this file; neither should have to move when the
 * other changes.
 */
import { optionKeysReaching, type EntryValues, type OptionKeyReader, type RecordSchemaResponse } from '@alluzo/shared';
import { and, eq, inArray, isNull } from 'drizzle-orm';

import { getDb } from '@/lib/db';
import { entryMirror, schemaCache } from '@/lib/db-schema';

export const valuesForEntryIds = (ids: readonly string[]): Map<string, EntryValues> => {
  const out = new Map<string, EntryValues>();
  if (ids.length === 0) return out;

  const rows = getDb()
    .select({ entryId: entryMirror.entryId, valuesJson: entryMirror.valuesJson })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.entryId, [...ids]),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();

  for (const row of rows) {
    try {
      out.set(row.entryId, JSON.parse(row.valuesJson) as EntryValues);
    } catch {
      // A row whose document will not parse is skipped, not thrown. One bad
      // mirror row must never stop a person opening a form.
    }
  }
  return out;
};

/**
 * One hop through the mirror: a field of the row an id names. For a rule the
 * form asks one field at a time — a show-when rule's hop — where loading a
 * set up front would load what is never asked.
 */
export const readMirrorHop = (entryId: string, fieldId: string): unknown =>
  valuesForEntryIds([entryId]).get(entryId)?.[fieldId];

/**
 * The schema this device holds for one record, or null. The read
 * `schemaFor` in entry-repo.ts makes, kept here for the reason above: the
 * choices a formula reads by key come from it, and a rule must stay testable.
 */
export const cachedSchemaOf = (recordId: string): RecordSchemaResponse | null => {
  const row = getDb()
    .select({ schemaJson: schemaCache.schemaJson })
    .from(schemaCache)
    .where(eq(schemaCache.recordId, recordId))
    .get();
  if (row === undefined) return null;
  try {
    return JSON.parse(row.schemaJson) as RecordSchemaResponse;
  } catch {
    return null;
  }
};

/**
 * `FormulaContext.optionKey` on this device (F4 of
 * docs/plans/field-relations.md): the choices of this record and of every
 * record its references point at, from the cached schemas. A `date_add` whose
 * unit is a select, of the row or across a hop, reads the choice's key by it.
 * Nothing is read until such a recipe asks.
 */
export const choiceKeysOf = (schema: RecordSchemaResponse | null): OptionKeyReader =>
  optionKeysReaching(schema, cachedSchemaOf);
